import { Menu } from "antd";
import { Button } from "components/Button/Button";
import { IconCollection } from "components/Icon/IconCollection";
import { showConfirmModal } from "components/Modal/showConfirmModal";
import {
    Action,
    ActionData,
    ActionSignature,
} from "containers/Resources/components/CommonActions/Action";
import { History, LocationState } from "history";
import { useDispatch, useSelector } from "react-redux";
import { Dispatch } from "redux";
import { actions as bundlesActions } from "./Bundles.reducer";
import {
    getBundlesFetching,
    getDisabledBundlesData,
    getSingleBundlesFetching,
} from "./Bundles.selectors";
import { BundleActionProps, BundleCommonActionProps, BundleConfirmModal } from "./interfaces";
import {
    canInstall,
    canPurge,
    canReInstall,
    canRelease,
    canUninstall,
    canUpdate,
    getButtonClassName,
} from "./utils";

/**
 * Confirmation copy for purging bundle(s) from the remote repository. Purge is
 * destructive and irreversible: it removes the released package and its listing
 * from the repo, leaving other workspaces that installed it unable to
 * re-install/update it. The local copy is untouched.
 */
const purgeConfirmModal = (
    bundles: BundleActionProps["resources"]
): BundleConfirmModal => {
    const target =
        bundles.length === 1
            ? `"${bundles[0]?.resourceData?.name}"`
            : `${bundles.length} bundles`;
    return {
        title: "Purge from repository",
        content: (
            <div>
                <p>
                    This permanently removes {target} from the remote bundle repository
                    (the released package and its repository listing).
                </p>
                <p>
                    Your local copy is not affected. Other workspaces that installed the
                    bundle keep their installation but can no longer re-install or update
                    it from this repository. This action cannot be undone.
                </p>
            </div>
        ),
        confirmText: "Purge from repository",
        cancelText: "Cancel",
        confirmButtonProps: { className: "button button--danger" },
    };
};

const BundleAction = ({
    selectedResources,
    actionButtonProps,
    type,
    afterAction,
    additionalVariables = {},
    isInlineButton,
    selectedName,
    confirmModal,
}: BundleCommonActionProps) => {
    const dispatch = useDispatch();
    const isFetching = useSelector(getBundlesFetching);

    const singleFetching = useSelector(getSingleBundlesFetching);
    const isSingleFetching =
        selectedResources.length > 0 &&
        isInlineButton &&
        selectedName &&
        singleFetching[selectedName] &&
        isFetching;

    const disabledBundlesData = useSelector(getDisabledBundlesData);

    const dispatchAction = (resources: BundleActionProps["resources"]) => {
        new Promise((resolve, reject) => {
            dispatch({
                type,
                promise: { resolve, reject },
                resources,
                afterAction,
                ...additionalVariables,
            });
        }).catch(console.error);
    };

    const bundleAction = (resources: BundleActionProps["resources"]) => {
        if (confirmModal) {
            showConfirmModal({
                ...confirmModal,
                onConfirm: () => dispatchAction(resources),
                className: "confirmationModal",
            });
        } else {
            dispatchAction(resources);
        }
    };

    const isActionRunning =
        isInlineButton && disabledBundlesData[selectedResources?.[0]?.resourceData?.key]
            ? true
            : undefined;

    return (
        <Button
            onClick={() => bundleAction(selectedResources)}
            disabled={!selectedResources.length || isSingleFetching || isActionRunning}
            icon={IconCollection.deleteOutline}
            {...actionButtonProps}
        />
    );
};

export const UninstallBundleListAction = ({
    resources,
    afterAction,
    buttonProps,
    isInlineButton,
    selectedName,
}: BundleActionProps) => {
    const installedBundles = resources.filter((data) => canUninstall(data));
    const className = getButtonClassName(!installedBundles.length, isInlineButton);
    const label = isInlineButton ? null : "Uninstall";
    const actionButtonProps = {
        ...buttonProps,
        className,
        label,
        icon: IconCollection.deleteOutline,
    };

    return (
        <BundleAction
            selectedResources={installedBundles}
            actionButtonProps={actionButtonProps}
            type={bundlesActions.START_UNINSTALL_BUNDLES}
            afterAction={afterAction}
            selectedName={selectedName}
            isInlineButton={isInlineButton}
        />
    );
};

export const InstallBundleListAction = ({
    resources,
    afterAction,
    buttonProps,
    isInlineButton,
    selectedName,
}: BundleActionProps) => {
    const uninstalledBundles = resources.filter(({ resourceData }) => canInstall(resourceData));
    const className = getButtonClassName(!uninstalledBundles.length, isInlineButton);
    const label = isInlineButton ? null : "Import";
    const actionButtonProps = {
        ...buttonProps,
        className,
        label,
        icon: IconCollection.fileImportOutline,
    };

    return (
        <BundleAction
            selectedResources={uninstalledBundles}
            actionButtonProps={actionButtonProps}
            type={bundlesActions.START_INSTALL_BUNDLES}
            afterAction={afterAction}
            selectedName={selectedName}
            isInlineButton={isInlineButton}
        />
    );
};

export const UpdateBundleListAction = ({
    resources,
    afterAction,
    buttonProps,
    isInlineButton,
    selectedName,
}: BundleActionProps) => {
    const updatedBundles = resources.filter((data) => canUpdate(data));
    const className = getButtonClassName(!updatedBundles.length, isInlineButton);
    const label = isInlineButton ? null : "Update";
    const actionButtonProps = {
        ...buttonProps,
        className,
        label,
        icon: IconCollection.autorenew,
    };

    return (
        <BundleAction
            selectedResources={updatedBundles}
            actionButtonProps={actionButtonProps}
            type={bundlesActions.START_INSTALL_BUNDLES}
            afterAction={afterAction}
            selectedName={selectedName}
            isInlineButton={isInlineButton}
        />
    );
};

export const ReInstallBundleListAction = ({
    resources,
    afterAction,
    buttonProps,
    isInlineButton,
    selectedName,
}: BundleActionProps) => {
    const installedBundles = resources.filter((data) => canReInstall(data));
    const className = getButtonClassName(!installedBundles.length, isInlineButton);
    const label = isInlineButton ? null : "Reinstall";
    const actionButtonProps = {
        ...buttonProps,
        className,
        label,
        icon: IconCollection.autorenew,
    };

    return (
        <BundleAction
            selectedResources={installedBundles}
            actionButtonProps={actionButtonProps}
            type={bundlesActions.START_INSTALL_BUNDLES}
            afterAction={afterAction}
            selectedName={selectedName}
            isInlineButton={isInlineButton}
        />
    );
};

export const ReleaseBundleListAction = ({
    resources,
    afterAction,
    buttonProps,
    isInlineButton,
    selectedName,
}: BundleActionProps) => {
    const updatedBundles = resources.filter((data) => canRelease(data));
    const className = getButtonClassName(!updatedBundles.length, isInlineButton);
    const label = isInlineButton ? null : "Release";
    const actionButtonProps = {
        ...buttonProps,
        className,
        label,
        icon: IconCollection.cubeSend,
    };

    return (
        <BundleAction
            selectedResources={updatedBundles}
            actionButtonProps={actionButtonProps}
            type={bundlesActions.START_RELEASE_BUNDLES}
            afterAction={afterAction}
            selectedName={selectedName}
            isInlineButton={isInlineButton}
        />
    );
};

export const PurgeBundleListAction = ({
    resources,
    afterAction,
    buttonProps,
    isInlineButton,
    selectedName,
}: BundleActionProps) => {
    const purgeableBundles = resources.filter((data) => canPurge(data));
    const className = getButtonClassName(!purgeableBundles.length, isInlineButton);
    const label = isInlineButton ? null : "Purge from repository";
    const actionButtonProps = {
        ...buttonProps,
        className,
        label,
        icon: IconCollection.deleteAlertOutline,
    };

    return (
        <BundleAction
            selectedResources={purgeableBundles}
            actionButtonProps={actionButtonProps}
            type={bundlesActions.START_PURGE_BUNDLES}
            afterAction={afterAction}
            selectedName={selectedName}
            isInlineButton={isInlineButton}
            confirmModal={purgeConfirmModal(purgeableBundles)}
        />
    );
};

interface SingleBundleActionProps {
    resource: ActionData;
    dispatch: Dispatch;
    type: string;
    history?: History<LocationState>;
}

const SingleBundleAction = ({ resource, dispatch, type, history }: SingleBundleActionProps) => {
    return new Promise((resolve, reject) => {
        dispatch({
            type,
            promise: { resolve, reject },
            resource,
            history,
        });
    }).catch(console.error);
};

export const ReleaseBundleAction: Action = (resource, dispatch, history) => {
    return SingleBundleAction({
        resource,
        dispatch,
        type: bundlesActions.START_RELEASE_SINGLE_BUNDLE,
    });
};
export const UninstallBundleAction: Action = (resource, dispatch, history) => {
    return SingleBundleAction({
        resource,
        dispatch,
        type: bundlesActions.START_UNINSTALL_SINGLE_BUNDLE,
        history,
    });
};
export const ReInstallBundleAction: Action = (resource, dispatch, history) => {
    return SingleBundleAction({
        resource,
        dispatch,
        type: bundlesActions.START_INSTALL_SINGLE_BUNDLE,
    });
};
export const UpdateBundleAction: Action = (resource, dispatch, history) => {
    return SingleBundleAction({
        resource,
        dispatch,
        type: bundlesActions.START_INSTALL_SINGLE_BUNDLE,
    });
};

export const PurgeBundleAction: Action = (resource, dispatch) =>
    new Promise((resolve, reject) => {
        showConfirmModal({
            ...purgeConfirmModal([{ resourceName: "bundle", resourceData: resource.resourceData }]),
            className: "confirmationModal",
            onConfirm: () => {
                SingleBundleAction({
                    resource,
                    dispatch,
                    type: bundlesActions.START_PURGE_SINGLE_BUNDLE,
                })
                    .then(resolve)
                    .catch(reject);
            },
            onCancel: () => reject("User cancelled purge"),
        });
    });

/**
 * Confirmation copy for a full git-maintenance run (gc + repack + prune) on a
 * bundle's git-sync bare repository. This is heavy and long-running (a large
 * bare can take several minutes) and briefly quiesces git-sync writes to this
 * bundle while it runs — hence the confirmation. It is NOT destructive: only
 * unreachable objects are pruned; committed content is untouched.
 */
const gitMaintenanceConfirmModal = (bundleName?: string): BundleConfirmModal => ({
    title: "Run git maintenance",
    content: (
        <div>
            <p>
                This runs a full <strong>git garbage-collect &amp; repack</strong> (with
                prune) on the git repository of{" "}
                {bundleName ? `"${bundleName}"` : "this bundle"} to reclaim disk space
                from loose and unreachable objects.
            </p>
            <p>
                It can take several minutes on a large repository and briefly pauses
                git-sync writes to this bundle while it runs. It is safe and
                non-destructive — committed history and records are untouched. Prefer to
                run it during low activity. To only reclaim already-unreachable objects
                cheaply, use “Prune loose objects only” from the dropdown instead.
            </p>
        </div>
    ),
    confirmText: "Run maintenance",
    cancelText: "Cancel",
});

/**
 * Dispatch a git-maintenance run (full or prune-only) for a single bundle and
 * return a promise that settles once the run is accepted by the backend.
 */
const dispatchGitMaintenance = ({
    resource,
    dispatch,
    body,
}: {
    resource: ActionData;
    dispatch: Dispatch;
    body: Record<string, boolean>;
}) =>
    new Promise((resolve, reject) => {
        dispatch({
            type: bundlesActions.START_GIT_MAINTENANCE_SINGLE_BUNDLE,
            promise: { resolve, reject },
            resource,
            body,
        });
    }).catch(console.error);

/**
 * Primary git-maintenance action: full gc/repack/prune, gated behind the
 * heavy-op confirmation dialog.
 */
export const GitMaintenanceAction: Action = (resource, dispatch) =>
    new Promise((resolve, reject) => {
        showConfirmModal({
            ...gitMaintenanceConfirmModal(resource.resourceData?.name),
            className: "confirmationModal",
            onConfirm: () => {
                dispatchGitMaintenance({
                    resource,
                    dispatch,
                    body: { do_gc: true, prune: true },
                })
                    .then(resolve)
                    .catch(reject);
            },
            // Dismissing the confirmation is not an error — settle quietly.
            onCancel: () => resolve(null),
        });
    });

/**
 * Dropdown menu item for the light "prune only" run — reclaims already
 * unreachable objects with negligible host impact, so it skips the heavy warning.
 */
export const gitMaintenancePruneMenuItem = ({
    actionData,
    dispatch,
}: {
    actionData: ActionData;
    dispatch: Dispatch;
}) => {
    const PruneIcon = IconCollection.autorenew;
    return (
        <Menu.Item
            key="gitMaintenancePruneOnly"
            icon={<PruneIcon size={16} />}
            onClick={() =>
                dispatchGitMaintenance({
                    resource: actionData,
                    dispatch,
                    body: { prune_only: true },
                })
            }
        >
            Prune loose objects only (light)
        </Menu.Item>
    );
};

export const releaseBundleActionKey = "releaseBundle";
export const uninstallBundleActionKey = "uninstallBundle";
export const updateBundleActionKey = "updateBundle";
export const reInstallBundleActionKey = "reInstallBundle";
export const purgeBundleActionKey = "purgeBundle";
export const gitMaintenanceActionKey = "gitMaintenance";

export const releaseBundleActionSignature: ActionSignature = {
    key: releaseBundleActionKey,
    action: ReleaseBundleAction,
    label: () => "Release",
    icon: () => IconCollection.cubeSend,
    omitWhen: (data: ActionData) => !canRelease(data),
    disableWhen: (data: ActionData) =>
        !!(
            data.currentBundleState &&
            data.currentBundleState.state === "STARTED" &&
            data.currentBundleState.operation === "RELEASE"
        ),
};
export const uninstallBundleActionSignature: ActionSignature = {
    key: uninstallBundleActionKey,
    action: UninstallBundleAction,
    label: () => "Uninstall",
    icon: () => IconCollection.deleteOutline,
    omitWhen: (data: ActionData) => !canUninstall(data),
    disableWhen: (data: ActionData) =>
        !!(
            data.currentBundleState &&
            data.currentBundleState.state === "STARTED" &&
            data.currentBundleState.operation === "UNINSTALL"
        ),
};
export const updateBundleActionSignature: ActionSignature = {
    key: updateBundleActionKey,
    action: UpdateBundleAction,
    label: () => "Update",
    icon: () => IconCollection.autorenew,
    omitWhen: (data: ActionData) => !canUpdate(data),
    disableWhen: (data: ActionData) =>
        !!(
            data.currentBundleState &&
            data.currentBundleState.state === "STARTED" &&
            data.currentBundleState.operation === "INSTALL"
        ),
};
export const relInstallBundleActionSignature: ActionSignature = {
    key: reInstallBundleActionKey,
    action: ReInstallBundleAction,
    label: () => "Reinstall",
    icon: () => IconCollection.autorenew,
    omitWhen: (data: ActionData) => !canReInstall(data),
    disableWhen: (data: ActionData) =>
        !!(
            data.currentBundleState &&
            data.currentBundleState.state === "STARTED" &&
            data.currentBundleState.operation === "INSTALL"
        ),
};
export const purgeBundleActionSignature: ActionSignature = {
    key: purgeBundleActionKey,
    action: PurgeBundleAction,
    label: () => "Purge from repository",
    icon: () => IconCollection.deleteAlertOutline,
    omitWhen: (data: ActionData) => !canPurge(data),
    disableWhen: (data: ActionData) =>
        !!(data.currentBundleState && data.currentBundleState.state === "STARTED"),
};
export const gitMaintenanceActionSignature: ActionSignature = {
    key: gitMaintenanceActionKey,
    action: GitMaintenanceAction,
    label: () => "Git maintenance",
    icon: () => IconCollection.tools,
    // Only meaningful for git-enabled bundles (those synced to a git bare repo).
    omitWhen: (data: ActionData) => !data.resourceData?.branch_name,
    // Avoid kicking off a heavy gc while another git write operation holds the lock.
    disableWhen: (data: ActionData) => !!data.isGitLocked,
    // Split button: main click = full maintenance (confirmed); dropdown = light prune-only.
    isDropdown: () => true,
    dropdownContent: ({ actionData, dispatch }) =>
        gitMaintenancePruneMenuItem({ actionData, dispatch }),
};
